import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPut, apiPost, fmtDate } from "@/lib/shieldx";
import { PageHeader, Loader } from "@/components/shieldx/Primitives";
import { Table } from "@/components/shieldx/Table";
import { StatusBadge } from "@/components/shieldx/Badges";
import { PencilBtn, ActionMenu, ActionDialog, EditDialog, Drawer, Timeline, KV, Section } from "@/components/shieldx/SuperAdmin";
import { ScrollText, Eye, Power, PowerOff } from "lucide-react";

export default function SAPolicies() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dl, setDl] = useState(false);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    setRows(await apiGet(`/super-admin/policies?${p.toString()}`));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [load]);

  const open360 = async (r) => { setSel(r); setDetail(null); setDl(true); try { setDetail(await apiGet(`/super-admin/policies/${r.id}/360`)); } finally { setDl(false); } };
  const runAction = async ({ reason, step_up_token }) => {
    const { r, act } = action;
    await apiPost(`/super-admin/policies/${r.id}/action`, { action: act, reason, step_up_token });
    toast.success(`${act} applied to ${r.name}`); load(); if (sel?.id === r.id) open360(r);
  };
  const saveEdit = async (vals) => { await apiPut(`/super-admin/policies/${edit.id}`, { ...vals, reason: "Policy edit" }); toast.success("Policy updated (new version)"); load(); };
  if (!rows) return <Loader label="Loading policies" />;

  const columns = [
    { key: "name", label: "Policy", render: (r) => <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.org_name} · v{r.version}</p></div> },
    { key: "scope", label: "Scope", render: (r) => <span className="text-xs text-slate-400">{r.scope}</span> },
    { key: "actions", label: "Actions", render: (r) => <span className="font-mono text-[11px] text-slate-400">{(r.actions || []).join(", ")}</span> },
    { key: "enabled", label: "State", render: (r) => <StatusBadge value={r.enabled ? "ACTIVE" : "DISABLED"} /> },
    { key: "act", label: "", render: (r) => (
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => open360(r)} data-testid={`policy-view360-${r.id}`} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-700/60 bg-slate-800/50 px-2.5 text-xs text-slate-300 hover:border-blue-500/50 hover:text-blue-300"><Eye className="h-3.5 w-3.5" /> 360°</button>
        <PencilBtn onClick={() => setEdit(r)} testid={`policy-edit-${r.id}`} />
        <ActionMenu testid={`policy-menu-${r.id}`} items={r.enabled
          ? [{ label: "Deactivate", icon: PowerOff, danger: true, onClick: () => setAction({ r, act: "DEACTIVATE" }) }]
          : [{ label: "Activate", icon: Power, onClick: () => setAction({ r, act: "ACTIVATE" }) }]} />
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader icon={ScrollText} title="Policies" subtitle="Authorization & security policy governance. Deactivating a policy is high-impact and requires password re-authentication." />
      <div className="mb-5"><input data-testid="policy-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search policies…" className="h-9 w-full max-w-sm rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" /></div>
      <Table columns={columns} rows={rows} testid="sa-policies-table" onRowClick={open360} empty="No policies" />

      <EditDialog open={!!edit} onClose={() => setEdit(null)} title={`Edit ${edit?.name || "policy"}`}
        fields={[{ key: "name", label: "Policy Name" }, { key: "scope", label: "Scope" }]} initial={edit || {}} onSave={saveEdit} />

      <ActionDialog open={!!action} onClose={() => setAction(null)} action={action?.act} targetType="POLICY" targetId={action?.r?.id}
        title={`${action?.act} — ${action?.r?.name || ""}`}
        body={action?.act === "DEACTIVATE" ? "Disabling a security policy is high-impact. Re-authenticate to proceed." : "This change is versioned and audited."} onDone={runAction} />

      <Drawer open={!!sel} onClose={() => { setSel(null); setDetail(null); }} loading={dl}
        title={sel?.name} subtitle={detail ? `${detail.policy.org_name} · v${detail.policy.version}` : ""} testid="policy-360-drawer"
        badge={detail && <StatusBadge value={detail.policy.enabled ? "ACTIVE" : "DISABLED"} />}
        tabs={detail ? [
          { key: "overview", label: "Overview", content: (
            <div className="grid gap-x-8 sm:grid-cols-2">
              <KV label="Policy ID" value={detail.policy.id} mono /><KV label="Organization" value={detail.policy.org_name} />
              <KV label="Scope" value={detail.policy.scope} /><KV label="Version" value={detail.policy.version} />
              <KV label="State" value={detail.policy.enabled ? "ACTIVE" : "DISABLED"} />
              <div className="sm:col-span-2"><Section title="Rules / Actions"><div className="flex flex-wrap gap-2">{(detail.policy.actions || []).map((a, i) => <span key={i} className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-1.5 font-mono text-xs text-slate-300">{a}</span>)}</div></Section></div>
            </div>
          ) },
          { key: "assets", label: `Affected Assets (${detail.affected_assets.length})`, content: (
            <Table testid="policy360-assets" empty="No affected assets" rows={detail.affected_assets} columns={[
              { key: "name", label: "Asset" }, { key: "type", label: "Type" }, { key: "state", label: "State", render: (r) => <StatusBadge value={r.state || r.protection_status} /> },
            ]} />
          ) },
          { key: "history", label: "Change History", content: (
            <Table testid="policy360-history" empty="No change history yet" rows={detail.change_history} columns={[
              { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs">{r.action}</span> }, { key: "actor", label: "Actor", render: (r) => <span className="text-xs">{r.actor}</span> },
              { key: "reason", label: "Reason", render: (r) => <span className="text-xs text-slate-400">{r.reason}</span> }, { key: "timestamp", label: "When", render: (r) => <span className="text-xs text-slate-500">{fmtDate(r.timestamp)}</span> },
            ]} />
          ) },
          { key: "timeline", label: "Timeline", content: <Timeline events={detail.timeline} /> },
        ] : []} />
    </div>
  );
}
